import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HOSTNAME_TEMPLATE_DEFAULT, defaultHostname, parseDomain, type EnrollVpsRequest, type JobDTO, type VpsDTO } from '@devrobot/shared';
import { ServerCog, ShieldCheck } from 'lucide-react';
import { api } from '../../api/client';
import { useApiMutation, useSettings } from '../../api/queries';
import { useUser } from '../../api/user';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IconChip, PageHeader, Section } from '../../components/PageHeader';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';

const NAME_RE = /^[a-z0-9][a-z0-9.-]{0,62}$/i;
type Method = 'password' | 'private-key' | 'local';

export default function VpsNewPage() {
  const user = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(22);
  const [username, setUsername] = useState('root');
  const [method, setMethod] = useState<Method>('password');
  const [password, setPassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const [retain, setRetain] = useState(false);
  const [proxyMode, setProxyMode] = useState<'managed-caddy' | 'none'>('managed-caddy');
  const [httpPort, setHttpPort] = useState(80);
  const [httpsPort, setHttpsPort] = useState(443);
  const [driver, setDriver] = useState<'docker' | 'simulate'>('docker');
  const [domain, setDomain] = useState('');
  const template = useSettings().data?.hostnameTemplate ?? HOSTNAME_TEMPLATE_DEFAULT;

  const enroll = useApiMutation((body: EnrollVpsRequest) => api.post<{ vps: VpsDTO; job: JobDTO }>('/api/v1/vps', body), { invalidate: [['vps'], ['overview']] });

  const changeMethod = (m: Method) => {
    setMethod(m);
    // Local development hosts usually cannot bind 80/443; suggest the alternative ports.
    if (m === 'local') {
      if (httpPort === 80) setHttpPort(18080);
      if (httpsPort === 443) setHttpsPort(18443);
      if (!host) setHost('localhost');
    } else {
      if (httpPort === 18080) setHttpPort(80);
      if (httpsPort === 18443) setHttpsPort(443);
    }
  };

  if (!user.platformAdmin) {
    return (
      <>
        <PageHeader crumbs={[{ label: 'VPS', to: '/vps' }, { label: 'Enroll' }]} title="Enroll a VPS" />
        <div role="note" className="alert alert-info alert-soft">
          Only platform administrators (GitLab group Owners or instance administrators) can enroll servers. <Link className="link" to="/vps">Back to the VPS list</Link>
        </div>
      </>
    );
  }

  const nameValid = NAME_RE.test(name);
  const portsValid = [port, httpPort, httpsPort].every((p) => Number.isInteger(p) && p > 0 && p < 65536) && httpPort !== httpsPort;
  const credValid = method === 'local' || (method === 'password' ? password.length > 0 : privateKey.trim().length > 0);
  const parsedDomain = domain.trim() ? parseDomain(domain) : null;
  const domainValid = !parsedDomain || parsedDomain.ok;
  const valid = nameValid && host.trim().length > 0 && portsValid && credValid && domainValid;

  const submit = () => {
    const auth: EnrollVpsRequest['auth'] =
      method === 'password' ? { method, password } : method === 'private-key' ? { method, privateKey, passphrase: passphrase || undefined } : { method: 'local' };
    enroll.mutate(
      {
        name,
        host: host.trim(),
        port,
        username: username.trim() || undefined,
        auth,
        expectedHostKeyFingerprint: fingerprint.trim() || undefined,
        retainBootstrapCredential: retain,
        proxyMode,
        edgePorts: { http: httpPort, https: httpsPort },
        driver,
        domain: parsedDomain?.ok ? parsedDomain.domain : undefined,
      },
      {
        onSuccess: (r) => {
          toast.success('Enrollment started', { description: 'Review the inspection report when it is ready; nothing is installed before you approve.' });
          navigate(`/jobs/${r.job.id}`);
        },
        onError: () => haptic('error'),
      },
    );
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'VPS', to: '/vps' }, { label: 'Enroll' }]}
        title="Enroll a VPS"
        icon={<IconChip icon={ServerCog} tone="primary" size="lg" />}
        subtitle="DevRobot connects once over SSH, inspects the server and shows you exactly what it will change. The agent is installed only after you approve; afterwards it connects out to this administrator, so no inbound management port is needed."
      />
      <form
        className="grid grid-cols-1 gap-6 xl:grid-cols-3"
        onInvalidCapture={() => haptic('error')}
        onSubmit={(e) => {
          e.preventDefault();
          if (enroll.isPending) return;
          if (!valid) {
            haptic('error');
            return;
          }
          submit();
        }}
      >
        <div className="space-y-6 xl:col-span-2">
          <Section title="Server">
            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Name</legend>
                <input className={`input w-full ${name && !nameValid ? 'input-error' : ''}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="eu-prod-1" required />
                <p className="label">Letters, digits, dots and dashes.</p>
              </fieldset>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Host</legend>
                <input className="input w-full font-mono" value={host} onChange={(e) => setHost(e.target.value)} placeholder="203.0.113.10 or vps.example.com" required autoCapitalize="off" spellCheck={false} />
              </fieldset>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">SSH port</legend>
                <input type="number" min={1} max={65535} className="input w-full" value={port} onChange={(e) => setPort(Number(e.target.value))} disabled={method === 'local'} />
              </fieldset>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">SSH user</legend>
                <input className="input w-full font-mono" value={username} onChange={(e) => setUsername(e.target.value)} disabled={method === 'local'} autoCapitalize="off" />
              </fieldset>
            </div>
          </Section>

          <Section title="Bootstrap credential" description="Used only to install the agent. It is sent to the DevRobot server and never shown again.">
            <div role="radiogroup" aria-label="Authentication method" className="tabs tabs-box tabs-sm w-fit max-w-full border border-hairline">
              {(
                [
                  ['password', 'Password'],
                  ['private-key', 'Private key'],
                  ['local', 'Local development host'],
                ] as const
              ).map(([m, label]) => (
                <label key={m} className={`tab ${method === m ? 'tab-active' : ''}`}>
                  <input type="radio" name="auth-method" className="sr-only" checked={method === m} onChange={() => changeMethod(m)} />
                  {label}
                </label>
              ))}
            </div>
            {method === 'password' ? (
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Password</legend>
                <input type="password" className="input w-full" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              </fieldset>
            ) : null}
            {method === 'private-key' ? (
              <>
                <fieldset className="fieldset">
                  <legend className="fieldset-legend">Private key</legend>
                  <textarea className="textarea w-full font-mono text-xs" rows={6} value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" spellCheck={false} autoComplete="off" />
                </fieldset>
                <fieldset className="fieldset">
                  <legend className="fieldset-legend">Passphrase (optional)</legend>
                  <input type="password" className="input w-full" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="new-password" />
                </fieldset>
              </>
            ) : null}
            {method === 'local' ? (
              <div role="note" className="alert alert-info alert-soft animate-rise text-sm">
                Installs the agent on the machine running the DevRobot server (development and demos only; the server must allow local enrollment). Edge ports default to 18080/18443 so they do not clash with local services.
              </div>
            ) : null}
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={retain} onChange={(e) => setRetain(e.target.checked)} disabled={method === 'local'} />
              <span>
                <span className="font-medium">Keep the credential for recovery</span>
                <span className="block text-sm text-base-content/65">
                  Off (recommended): the credential is discarded after enrollment and only the agent’s outbound connection is used. On: stored encrypted so an administrator can recover the server over SSH if the agent stops responding.
                </span>
              </span>
            </label>
          </Section>

          <Section title="Host identity" description="Protects against connecting to the wrong machine.">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Expected SSH host key fingerprint (optional)</legend>
              <input className="input w-full font-mono" value={fingerprint} onChange={(e) => setFingerprint(e.target.value)} placeholder="SHA256:…" autoCapitalize="off" spellCheck={false} disabled={method === 'local'} />
              <p className="label whitespace-normal">
                If you provide it (from your provider’s console), DevRobot stops unless the server presents exactly this key. If you leave it empty, DevRobot records the key it sees and shows it on the approval screen for you to verify before anything is installed.
              </p>
            </fieldset>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Public traffic">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Reverse proxy</legend>
              <select className="select w-full" value={proxyMode} onChange={(e) => setProxyMode(e.target.value as 'managed-caddy' | 'none')}>
                <option value="managed-caddy">Managed Caddy (HTTPS and hostnames)</option>
                <option value="none">None (host ports only)</option>
              </select>
              <p className="label whitespace-normal">
                {proxyMode === 'managed-caddy'
                  ? 'DevRobot runs Caddy for certificates and routing. If the ports below are already in use, the inspection reports it and nothing is replaced without your approval.'
                  : 'Applications can only be exposed through explicit host ports.'}
              </p>
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">HTTP port</legend>
                <input type="number" className="input w-full" value={httpPort} onChange={(e) => setHttpPort(Number(e.target.value))} disabled={proxyMode === 'none'} />
              </fieldset>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">HTTPS port</legend>
                <input type="number" className="input w-full" value={httpsPort} onChange={(e) => setHttpsPort(Number(e.target.value))} disabled={proxyMode === 'none'} />
              </fieldset>
            </div>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Domain (optional)</legend>
              <input
                className={`input w-full font-mono ${domainValid ? '' : 'input-error'}`}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="eu1.devrobot.dev"
                autoCapitalize="off"
                spellCheck={false}
                disabled={proxyMode === 'none'}
              />
              <p className="label whitespace-normal">
                {parsedDomain && !parsedDomain.ok ? (
                  <span className="dr-ink-error">{parsedDomain.error}</span>
                ) : parsedDomain?.ok ? (
                  <span>
                    Applications get a hostname such as <code className="break-anywhere font-mono">{defaultHostname(template, { project: 'erp', environment: 'development' }, parsedDomain.domain)}</code>. Create one DNS record:{' '}
                    <code className="break-anywhere font-mono">
                      *.{parsedDomain.domain} A {/^\d+\.\d+\.\d+\.\d+$/.test(host.trim()) ? host.trim() : '<public IP>'}
                    </code>
                  </span>
                ) : (
                  'Applications deployed here get a hostname under this domain automatically. You can set or change it later.'
                )}
              </p>
            </fieldset>
          </Section>
          <details className="collapse collapse-arrow card dr-surface">
            <summary className="collapse-title font-medium">Advanced</summary>
            <div className="collapse-content">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Runtime driver</legend>
                <select className="select w-full" value={driver} onChange={(e) => setDriver(e.target.value as 'docker' | 'simulate')}>
                  <option value="docker">docker (real containers)</option>
                  <option value="simulate">simulate (no containers; demos and tests)</option>
                </select>
              </fieldset>
            </div>
          </details>
          <ErrorAlert error={enroll.error} title="Enrollment was not started" />
          <div className="flex flex-col gap-2">
            <Haptic className="w-full" disabled={!valid || enroll.isPending}>
              <button type="submit" className="btn btn-block btn-primary" disabled={!valid || enroll.isPending}>
                {enroll.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <ShieldCheck className="size-4" aria-hidden="true" />}
                Start enrollment
              </button>
            </Haptic>
            <p className="text-xs text-base-content/60">Next: DevRobot validates the connection and inspects the host, then waits for your approval on the job page.</p>
          </div>
        </div>
      </form>
    </>
  );
}
